import { syncTypes } from '~/sqlite/syncDataTypes';

import { machineInternForQueueRow } from './syncProgressQueueDetails';
import { SyncQueueRow } from './syncQueueTypes';
import {
  idleSyncProgressState,
  SyncProgressItem,
  SyncProgressItemType,
  SyncProgressRunItem,
  SyncProgressState,
} from './syncProgressTypes';

type SyncProgressListener = (state: SyncProgressState) => void;

let state: SyncProgressState = { ...idleSyncProgressState, items: [] };
const listeners = new Set<SyncProgressListener>();

function snapshot(): SyncProgressState {
  return {
    ...state,
    items: state.items.map((item) => ({ ...item })),
  };
}

function emit(): void {
  const snap = snapshot();
  listeners.forEach((listener) => listener(snap));
}

export function getSyncProgressState(): SyncProgressState {
  return snapshot();
}

export function subscribeSyncProgress(listener: SyncProgressListener): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => listeners.delete(listener);
}

export function mapQueueTypeToProgressType(typeOfData: string): SyncProgressItemType | null {
  switch (typeOfData) {
    case syncTypes.ADD_ISSUE:
      return 'issue';
    case syncTypes.UPDATE_OPERATING_DATA:
      return 'operating_hours';
    case syncTypes.ADD_CHECK_PERIOD:
      return 'periodic_check';
    default:
      return null;
  }
}

export function countIssuePhotosFromQueueRow(images: string | null): number {
  if (!images || images === '[]' || images === '') {
    return 0;
  }
  try {
    const parsed = JSON.parse(images) as unknown;
    if (!Array.isArray(parsed)) {
      return 0;
    }
    return parsed.filter((entry) => typeof entry === 'string' && entry.length > 0).length;
  } catch {
    return 0;
  }
}

/** Build run items from queue rows for SyncManager. */
export async function syncProgressRunItemsFromQueueRows(
  rows: SyncQueueRow[]
): Promise<SyncProgressRunItem[]> {
  const internByMachineId = new Map<string, string | undefined>();
  const items: SyncProgressRunItem[] = [];
  for (const row of rows) {
    const type = mapQueueTypeToProgressType(row.typeOfData);
    if (!type) {
      continue;
    }
    const machineIntern = await machineInternForQueueRow(row, internByMachineId);
    items.push({
      id: row.id,
      type,
      ...(machineIntern ? { machineIntern } : {}),
    });
  }
  return items;
}

function runItemsToProgressItems(runItems: SyncProgressRunItem[]): SyncProgressItem[] {
  return runItems.map((item) => ({
    id: item.id,
    type: item.type,
    ...(item.machineIntern ? { machineIntern: item.machineIntern } : {}),
    status: 'waiting',
  }));
}

export function isSyncRunInProgress(): boolean {
  return state.status === 'syncing';
}

export function isSyncRunPaused(): boolean {
  return state.status === 'paused';
}

/** Session start: an item left mid-upload is not a failure. */
export function resetUploadingToWaiting(): void {
  if (!state.items.some((item) => item.status === 'uploading')) {
    return;
  }
  state = {
    ...state,
    items: state.items.map((item) =>
      item.status === 'uploading' ? { ...item, status: 'waiting' } : item
    ),
  };
  emit();
}

/**
 * Fresh run only. No-op when the queue is empty or a run is already tracked
 * (`paused`, `syncing`, `success`, `partial_failure`) — callers resume or append instead.
 */
export function startRun(runItems: SyncProgressRunItem[]): void {
  if (!runItems.length || state.status !== 'idle') {
    return;
  }

  state = {
    status: 'syncing',
    total: runItems.length,
    completed: 0,
    items: runItemsToProgressItems(runItems),
  };
  emit();
}

/** Continue a paused run. Confirmed (`done`) items stay done. */
export function resumeRun(): void {
  if (state.status !== 'paused') {
    return;
  }
  state = { ...state, status: 'syncing' };
  emit();
}

/**
 * Queue rows that appeared after the current run started (for example saved while paused).
 * From `success` / `partial_failure`, switches back to `syncing` without clearing failures.
 */
export function includeAdditionalWaitingItems(runItems: SyncProgressRunItem[]): void {
  if (
    state.status !== 'syncing' &&
    state.status !== 'paused' &&
    state.status !== 'success' &&
    state.status !== 'partial_failure'
  ) {
    return;
  }
  const existing = new Set(state.items.map((item) => item.id));
  const extra = runItemsToProgressItems(runItems.filter((item) => !existing.has(item.id)));
  if (!extra.length) {
    return;
  }
  state = {
    ...state,
    status: state.status === 'paused' ? 'paused' : 'syncing',
    total: state.total + extra.length,
    items: [...state.items, ...extra],
  };
  emit();
}

/**
 * Re-queue failed items only. Returns false when nothing failed or a run is active.
 */
export function retryFailed(): boolean {
  if (state.status === 'syncing') {
    return false;
  }
  const failedCount = state.items.filter((item) => item.status === 'failed').length;
  if (!failedCount) {
    return false;
  }
  state = {
    ...state,
    status: 'syncing',
    completed: Math.max(0, state.completed - failedCount),
    items: state.items.map((item) =>
      item.status === 'failed' ? { ...item, status: 'waiting' } : item
    ),
  };
  emit();
  return true;
}

export function markUploading(id: number): void {
  if (state.status !== 'syncing') {
    return;
  }
  const index = state.items.findIndex((item) => item.id === id);
  if (index === -1) {
    return;
  }
  const items = state.items.slice();
  items[index] = { ...items[index], status: 'uploading' };
  state = { ...state, items };
  emit();
}

function markItemTerminal(id: number, status: 'done' | 'failed'): void {
  const index = state.items.findIndex((item) => item.id === id);
  if (index === -1) {
    return;
  }
  const items = state.items.slice();
  const previous = items[index];
  if (previous.status === 'done' || previous.status === 'failed') {
    return;
  }
  items[index] = { ...previous, status };
  const completed =
    state.completed + (status === 'done' || status === 'failed' ? 1 : 0);
  state = { ...state, items, completed };
  emit();
}

export function markDone(id: number): void {
  if (state.status !== 'syncing') {
    return;
  }
  markItemTerminal(id, 'done');
}

export function markFailed(id: number): void {
  if (state.status !== 'syncing') {
    return;
  }
  markItemTerminal(id, 'failed');
}

function revertUploadingToWaiting(): void {
  if (!state.items.some((item) => item.status === 'uploading')) {
    return;
  }
  state = {
    ...state,
    items: state.items.map((item) =>
      item.status === 'uploading' ? { ...item, status: 'waiting' } : item
    ),
  };
}

/** Network drop: in-flight item goes back to waiting. Not a failure. */
export function markPaused(): void {
  if (state.status !== 'syncing') {
    return;
  }
  revertUploadingToWaiting();
  state = { ...state, status: 'paused' };
  emit();
}

export function markFinished(): void {
  if (state.status !== 'syncing') {
    return;
  }
  const hasFailure = state.items.some((item) => item.status === 'failed');
  state = {
    ...state,
    status: hasFailure ? 'partial_failure' : 'success',
  };
  emit();
}

export function reset(): void {
  state = { ...idleSyncProgressState, items: [] };
  emit();
}
